// /tutorial-demo?reel — the 9:16 short. A 1080×1920 canvas: scene headline on top, the app
// (compact 720×1000 stage at 1.5×) in the middle, word-timed captions at the bottom.
// `?reel&render` is the recording mode: canvas at 1:1, no poster or controls, and
// window.__demo for the headless recorder to drive it.
import React, {useEffect, useMemo, useState} from 'react'
import clsx from 'clsx'
import {LoginScreen, AppScreen} from './DemoScreens'
import {useDemoEngine} from './useDemoEngine'
import {REEL_SCENES, REEL_STAGE_W, REEL_STAGE_H} from './reelScript'

const CANVAS_W = 1080
const CANVAS_H = 1920
const HEADER_H = 230
const STAGE_SCALE = CANVAS_W / REEL_STAGE_W
const CAPTION_TOP = HEADER_H + REEL_STAGE_H * STAGE_SCALE

// On DO Spaces (served with Content-Disposition: attachment, so the link downloads it).
export const REEL_MP4 = 'https://zipper.ams3.cdn.digitaloceanspaces.com/zipper/site-media/fbzipper/tutorial/fbzipper-reel.mp4'

type Word = [string, number, number]

// Caption lines: a break after punctuation, or every four words.
const toChunks = (words: Word[]): Word[][] => {
  const chunks: Word[][] = []
  let cur: Word[] = []
  words.forEach((w) => {
    cur.push(w)
    if (/[,.?!:]$/.test(w[0]) || cur.length >= 4) {
      chunks.push(cur)
      cur = []
    }
  })
  if (cur.length) chunks.push(cur)
  return chunks
}

// embed: inside the /landing video dialog — no download / full-tutorial links under it.
export const ReelDemo: React.FC<{embed?: boolean}> = ({embed}) => {
  const render = new URLSearchParams(window.location.search).has('render')
  const engine = useDemoEngine(REEL_SCENES, REEL_STAGE_W, REEL_STAGE_H)
  const {
    t, total, local, scene, state, playing, started, sceneStart,
    play, toggle, seek,
    stageRef, contentRef, cursorRef, rippleRef, spotRef,
  } = engine
  const [fit, setFit] = useState(1)

  // Fit the canvas to the window (height-first — it's a phone-shaped video).
  useEffect(() => {
    if (render) return
    const onResize = () => setFit(Math.min((window.innerHeight - 110) / CANVAS_H, (window.innerWidth - 32) / CANVAS_W))
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [render])

  // Recording hook for the headless recorder.
  useEffect(() => {
    if (!render) return
    ;(window as any).__demo = {
      play, seek, total, t, playing,
      // Where each narration clip starts — the recorder lays the audio track from these.
      clips: REEL_SCENES.map((sc, i) => ({audio: sc.audio, start: sceneStart(i)})),
    }
  }, [render, play, seek, total, t, playing, sceneStart])

  const chunks = useMemo(() => toChunks(scene.words || []), [scene])
  const chunk = [...chunks].reverse().find((c) => c[0][1] <= local) || chunks[0] || []

  const canvas = (
    <div className='tr-canvas' style={{width: CANVAS_W, height: CANVAS_H}} dir='rtl'>
      <div className='tr-header' style={{height: HEADER_H}}>
        <img src='/media/logos/d-code-logo-light.svg' alt='FB Zipper' className='tr-logo' />
        <div key={scene.id} className='tr-headline'>{scene.headline}</div>
      </div>

      <div className='tr-stage-wrap' style={{top: HEADER_H, width: CANVAS_W, height: REEL_STAGE_H * STAGE_SCALE}} dir='ltr'>
        <div className='td-stage td-compact' ref={stageRef} style={{width: REEL_STAGE_W, height: REEL_STAGE_H, transform: `scale(${STAGE_SCALE})`}}>
          {state.screen === 'login' ? <LoginScreen s={state} /> : <AppScreen s={state} contentRef={contentRef} />}
          <div className='td-spot' ref={spotRef} />
          <div className='td-ripple' ref={rippleRef} />
          <div className='td-cursor' ref={cursorRef}>
            <svg width='28' height='28' viewBox='0 0 24 24'>
              <path d='M4 2l15 11.2-6.6 1.1 3.9 7.3-2.9 1.5-3.9-7.4L4 20.5z' fill='#111' stroke='#fff' strokeWidth='1.4' strokeLinejoin='round' />
            </svg>
          </div>
        </div>
      </div>

      <div className='tr-captions' style={{top: CAPTION_TOP, height: CANVAS_H - CAPTION_TOP}}>
        <div className='tr-progress'><div style={{width: `${(t / total) * 100}%`}} /></div>
        <div className='tr-caption-line' key={`${scene.id}-${chunk[0]?.[1]}`}>
          {chunk.map((w, i) => (
            <span key={i} className={clsx(w[1] <= local && 'on')}>{w[0]} </span>
          ))}
        </div>
      </div>

      {state.outro && (
        <div className='tr-outro'>
          <img src='/media/logos/d-code-logo-light.svg' alt='FB Zipper' />
          <div className='tr-outro-line'>אתם מוסיפים פוסט,<br />אנחנו דואגים לכל השאר.</div>
          <div className='tr-outro-cta'>נסו עכשיו ←</div>
          <div className='tr-outro-url'>fbzipper.com</div>
          <div className='tr-caption-line tr-outro-caption'>
            {chunk.map((w, i) => (
              <span key={i} className={clsx(w[1] <= local && 'on')}>{w[0]} </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )

  if (render) return <div className='tr-render'>{canvas}</div>

  return (
    <div className='tr-page' dir='rtl'>
      <div className='tr-frame' style={{width: CANVAS_W * fit, height: CANVAS_H * fit}} onClick={toggle}>
        {/* Pinned left: in an RTL page the 1080px canvas would otherwise hang off the frame's
            right edge, and scaling from the top-left then pushes it out of view. */}
        <div style={{position: 'absolute', top: 0, left: 0, transform: `scale(${fit})`, transformOrigin: 'top left'}}>{canvas}</div>
        {!playing && (
          <div className='tr-play'>
            <span className='td-poster-play'>
              <svg viewBox='0 0 24 24' width='44' height='44'><path d='M8 5v14l11-7z' fill='#fff' /></svg>
            </span>
            {!started && <span className='tr-play-title'>צפו בסרטון (40 שניות)</span>}
          </div>
        )}
      </div>
      {!embed && (
      <div className='tr-links'>
        <a href={REEL_MP4} download className='tr-link primary'>
          <i className='bi bi-download'></i> הורדת הסרטון (MP4)
        </a>
        <a href='/tutorial-demo' className='tr-link'>למדריך המלא</a>
      </div>
      )}
    </div>
  )
}
